import type { IExpectedHoursItem } from "../../services/types/expectedHours";
import type { WorktimeSettlementStatus } from "../../services/types/worktimeSettlement";
import {
  clearanceStatusText,
  delayedAcceptanceStatusText,
  formatCalendarDate,
} from "../../utils/cooperationEnd";

type RowTone = "neutral" | "attention";

type ComplianceIconKind = "consistent" | "inconsistent";

type ComplianceMeta = {
  iconKind: ComplianceIconKind;
  label: string;
};

type DecisionBadgePresentation = {
  label: string;
  color: "informative" | "success" | "danger";
  appearance: "tint";
};

const DECISION_BADGE_PRESENTATION: Record<WorktimeSettlementStatus, DecisionBadgePresentation> = {
  pending: { label: "Oczekuje", color: "informative", appearance: "tint" },
  accepted: { label: "Zaakceptowano", color: "success", appearance: "tint" },
  rejected: { label: "Odrzucono", color: "danger", appearance: "tint" },
};

const ACCEPT_BLOCK_REASON = {
  workTimeWithoutOrders:
    "Zalogowano czas w YouTrack pomimo braku zlecenia w alokacji zasobów",
  workTimeOutsideOrders:
    "Zalogowano czas w YouTrack w projekcie bez zlecenia w alokacji zasobów",
  pendingManagers:
    "Oczekuje na akceptację godzin przez kierowników projektu",
  rejectedByManager:
    "Odrzucono godziny przez kierownika projektu",
  ordersNotAccepted:
    "Nie wszystkie zlecenia w alokacji zasobów zostały zaakceptowane",
  delayedAcceptance:
    "Wymagane rozliczenie odroczonego odbioru usług",
} as const;

export function resolveAdminDecision(item: IExpectedHoursItem): WorktimeSettlementStatus {
  return item.settlementStatus ?? "pending";
}

export function resolveRowTone(item: IExpectedHoursItem): RowTone {
  const decision = resolveAdminDecision(item);
  if (decision === "rejected") return "attention";
  if (decision === "pending" && !item.isValuesConsistent) return "attention";
  if (item.hasIncompleteWorkHours
      && item.resourceAllocationSettlementStatus === "pending") return "attention";
  if (item.hasWorkTimeWithoutOrders) return "attention";
  if (item.hasWorkTimeOutsideOrders) return "attention";
  if (item.cooperationEndBlocksAcceptance && decision !== "accepted") return "attention";
  return "neutral";
}

export function getComplianceMeta(item: IExpectedHoursItem): ComplianceMeta {
  const decision = resolveAdminDecision(item);
  const ok = item.isValuesConsistent;

  if (ok && decision === "pending") {
    return { iconKind: "consistent", label: "Dane zgodne" };
  }
  if (!ok && decision === "pending") {
    return { iconKind: "inconsistent", label: "Wykryto rozbieżność" };
  }
  if (ok && decision === "accepted") {
    return { iconKind: "consistent", label: "Dane zgodne" };
  }
  if (!ok && decision === "accepted") {
    return { iconKind: "inconsistent", label: "Zaakceptowano mimo rozbieżności" };
  }
  if (ok && decision === "rejected") {
    return { iconKind: "consistent", label: "Dane zgodne" };
  }
  return { iconKind: "inconsistent", label: "Odrzucono z rozbieżnością" };
}

export function resolveDecisionBadgePresentation(
  decision: WorktimeSettlementStatus
): DecisionBadgePresentation {
  return DECISION_BADGE_PRESENTATION[decision];
}

export function resolveYouTrackSettlementMeta(item: IExpectedHoursItem): ComplianceMeta | null {
  if (!item.hasIncompleteWorkHours) {
    return null;
  }

  if (item.hasWorkTimeWithoutOrders) {
    return { iconKind: "inconsistent", label: ACCEPT_BLOCK_REASON.workTimeWithoutOrders };
  }

  if (item.hasWorkTimeOutsideOrders) {
    return { iconKind: "inconsistent", label: ACCEPT_BLOCK_REASON.workTimeOutsideOrders };
  }

  const status = item.resourceAllocationSettlementStatus;

  if (status === "pending") {
    return { iconKind: "inconsistent", label: ACCEPT_BLOCK_REASON.pendingManagers };
  }

  if (status === "rejected") {
    return { iconKind: "inconsistent", label: ACCEPT_BLOCK_REASON.rejectedByManager };
  }

  return { iconKind: "consistent", label: "Zaakceptowano przez kierowników projektu" };
}

function isAllocationBlocked(item: IExpectedHoursItem): boolean {
  return item.hasIncompleteWorkHours && !item.canAcceptMainSettlement;
}

export function isAcceptBlocked(item: IExpectedHoursItem): boolean {
  return isAllocationBlocked(item) || item.cooperationEndBlocksAcceptance;
}

export function earlyServiceEndWarningLabel(item: IExpectedHoursItem): string | null {
  if (!item.showEarlyServiceEndWarning) {
    return null;
  }

  const date = formatCalendarDate(item.serviceEndDate);
  return date
    ? `Umowa kończy się w pierwszej połowie przyszłego miesiąca (${date})`
    : "Umowa kończy się w pierwszej połowie przyszłego miesiąca";
}

export function cooperationEndStatusLines(item: IExpectedHoursItem): string[] | null {
  if (!item.showCooperationEndStatus) {
    return null;
  }

  return [
    clearanceStatusText(
      item.clearanceSettlementState,
      item.clearanceSettledAt,
      item.clearanceSettledByName
    ),
    delayedAcceptanceStatusText(
      item.delayedAcceptanceSettlementState,
      item.delayedAcceptanceSettledAt,
      item.delayedAcceptanceSettledByName
    ),
  ];
}

export function resolveAcceptBlockedReasons(item: IExpectedHoursItem): string[] {
  if (!isAcceptBlocked(item) || resolveAdminDecision(item) === "accepted") {
    return [];
  }

  const reasons: string[] = [];

  if (item.cooperationEndClearanceBlocksAcceptance) {
    const date = formatCalendarDate(item.serviceEndDate);
    reasons.push(
      date
        ? `Wymagane rozliczenie obiegówki — zakończenie usług ${date}`
        : "Wymagane rozliczenie obiegówki — zakończenie usług"
    );
  }

  if (item.cooperationEndDelayedAcceptanceBlocksAcceptance) {
    reasons.push(ACCEPT_BLOCK_REASON.delayedAcceptance);
  }

  if (!isAllocationBlocked(item)) {
    return reasons;
  }

  const allocationReasons: string[] = [];

  if (item.hasWorkTimeWithoutOrders) {
    allocationReasons.push(ACCEPT_BLOCK_REASON.workTimeWithoutOrders);
  }

  if (item.hasWorkTimeOutsideOrders) {
    allocationReasons.push(ACCEPT_BLOCK_REASON.workTimeOutsideOrders);
  }

  if (item.resourceAllocationSettlementStatus === "pending") {
    allocationReasons.push(ACCEPT_BLOCK_REASON.pendingManagers);
  }

  if (item.resourceAllocationSettlementStatus === "rejected") {
    allocationReasons.push(ACCEPT_BLOCK_REASON.rejectedByManager);
  }

  if (allocationReasons.length === 0) {
    allocationReasons.push(ACCEPT_BLOCK_REASON.ordersNotAccepted);
  }

  return [...reasons, ...allocationReasons];
}
